import type { CollectionQueryRequest } from "~/api/generated/record-service/model/collectionQueryRequest";
import type { ParentScopedReachableChildrenQueryRequest } from "~/api/generated/record-service/model/parentScopedReachableChildrenQueryRequest";
import type { ParentScopedReachableHop } from "~/api/generated/record-service/model/parentScopedReachableHop";
import {
  getQueryChildrenUnderParentUrl,
  getQueryReachableChildrenUnderParentUrl,
  queryChildrenUnderParent,
  queryReachableChildrenUnderParent,
} from "~/api/generated/record-service/record/record";
import { getChildUnderParent } from "~/api/generated/record-service/relationship/relationship";
import { ApiError } from "~/lib/apiClient";

import { extractAuditMetadata, parseRecordDetail } from "../api/recordParsers";
import type { BusinessObjectMetadata } from "../utils/normalizeMetadata";

/**
 * Parent-scoped child collection (CPES-16868 / CPES-16879 / CPES-16897 / CPES-16937).
 *
 * A direct outgoing edge posts (`queryChildrenUnderParent`):
 *
 * `POST /record-service/v1/{parent}/{parentVersion}/{parentPk}/relationships/{child}/{childVersion}/children/{child}/{childVersion}/query`
 *
 * A child reached through two or more declared hops posts
 * (`queryReachableChildrenUnderParent`):
 *
 * `POST /record-service/v1/{parent}/{parentVersion}/{parentPk}/related/{child}/{childVersion}/query`
 *
 * with the same collection-query body plus `path`. Each hop may set `direction`
 * to `incoming` or `outgoing` (omitted means outgoing). The generated path item
 * does not list `direction` yet, so the body is cast at the call. Do not put
 * `parent` in the body, and do not also send the old `id in [...]` gate. A 404
 * means the parent is missing or not visible. Callers must not fall back to the
 * global collection.
 *
 * On the one-hop route `{relationshipName}` must equal `{childResource}`.
 * `{relationshipVersion}` is the child schema version.
 *
 * Opening one of those children is `getChildUnderParent` on the same path plus
 * `{childPk}`. A 404 means the parent or the edge is missing. Do not retry it
 * as `GET /{child}/{pk}`. Multi-hop paths have no point-read route.
 */

/** One hop target. Version defaults to `1.0.0` when the slot omits it. */
export interface ParentScopedPathHop {
  resource: string;
  version: string;
  /**
   * Omitted means outgoing. `incoming` walks an edge that points at the current record.
   */
  direction?: ParentScopedReachableHop["direction"];
}

/** Same hop shape. Existing imports use this name. */
export type ReachablePathHop = ParentScopedPathHop;

export interface ParentScopedChildQuery {
  parentResource: string;
  parentVersion: string;
  parentPk: string;
  relationship: string;
  relationshipVersion: string;
  childResource: string;
  childVersion: string;
  /**
   * Set when the child is not a direct edge (length 2 or more). The last hop's
   * resource is `childResource`.
   */
  path?: readonly ParentScopedPathHop[];
}

/** The reachable route's path, or `undefined` for a direct edge. */
const reachablePath = (
  scope: ParentScopedChildQuery,
): readonly ParentScopedPathHop[] | undefined =>
  scope.path && scope.path.length >= 2 ? scope.path : undefined;

/**
 * Generated URL builders interpolate path params as given, so every segment is
 * encoded here. A pk containing `/` must stay one segment.
 */
const segment = (value: string): string => encodeURIComponent(value);

type OneHopArgs = Parameters<typeof getQueryChildrenUnderParentUrl>;
type ReachableArgs = Parameters<typeof getQueryReachableChildrenUnderParentUrl>;

const oneHopArgs = (scope: ParentScopedChildQuery): OneHopArgs => [
  segment(scope.parentResource),
  segment(scope.parentVersion),
  segment(scope.parentPk),
  segment(scope.relationship),
  segment(scope.relationshipVersion),
  segment(scope.childResource),
  segment(scope.childVersion),
];

const reachableBody = (
  body: CollectionQueryRequest,
  path: readonly ParentScopedPathHop[],
): ParentScopedReachableChildrenQueryRequest => ({
  ...body,
  path: path.map((hop) => ({
    resource: hop.resource,
    version: hop.version,
    ...(hop.direction ? { direction: hop.direction } : {}),
  })),
});

const reachableArgs = (scope: ParentScopedChildQuery): ReachableArgs => [
  segment(scope.parentResource),
  segment(scope.parentVersion),
  segment(scope.parentPk),
  segment(scope.childResource),
  segment(scope.childVersion),
];

/** Path the collection slot posts when {@link ParentScopedChildQuery} is set. */
export const parentScopedChildrenQueryPath = (scope: ParentScopedChildQuery): string =>
  reachablePath(scope)
    ? getQueryReachableChildrenUnderParentUrl(...reachableArgs(scope))
    : getQueryChildrenUnderParentUrl(...oneHopArgs(scope));

/** Multi-hop parent-path children URL. */
export const parentScopedRelatedQueryPath = (scope: ParentScopedChildQuery): string =>
  getQueryReachableChildrenUnderParentUrl(...reachableArgs(scope));

/**
 * Posts the parent-path children query. A 404 is returned to the caller.
 * Do not retry it as `POST /{child}/query`.
 */
export const postParentScopedChildrenQuery = async (
  scope: ParentScopedChildQuery,
  body: CollectionQueryRequest,
  headers: Record<string, string> | undefined,
  signal?: AbortSignal,
): Promise<unknown> => {
  const options: RequestInit = {
    ...(headers ? { headers } : {}),
    ...(signal ? { signal } : {}),
  };
  const path = reachablePath(scope);
  const response = path
    ? await queryReachableChildrenUnderParent(
        ...reachableArgs(scope),
        reachableBody(body, path),
        options,
      )
    : await queryChildrenUnderParent(...oneHopArgs(scope), body, options);
  return response.data;
};

/** Fields a peek target or navigation state can carry. `path` is only present on a query scope. */
export type ParentPointRead = Pick<
  ParentScopedChildQuery,
  | "parentResource"
  | "parentVersion"
  | "parentPk"
  | "relationship"
  | "relationshipVersion"
  | "childResource"
  | "childVersion"
> & { path?: readonly ParentScopedPathHop[] };

/**
 * The one-hop point read. A multi-hop `path` has no `getChildUnderParent`
 * route, and a relationship name that is not the child schema is a 400.
 */
export const directParentRead = (
  scope: ParentPointRead | undefined,
): ParentScopedChildQuery | undefined => {
  if (!scope || reachablePath(scope)) {
    return undefined;
  }
  if (
    scope.relationship !== scope.childResource ||
    scope.relationshipVersion !== scope.childVersion
  ) {
    return undefined;
  }

  return scope;
};

/** Navigation state for a row opened from a parent-scoped collection. A copied URL omits it. */
export const PARENT_CHILD_READ_STATE = "parentChildRead";

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const readFields = (scope: ParentPointRead): ParentScopedChildQuery | undefined => {
  if (
    !scope.parentResource ||
    !scope.parentVersion ||
    !scope.parentPk ||
    !scope.relationship ||
    !scope.relationshipVersion ||
    !scope.childResource ||
    !scope.childVersion
  ) {
    return undefined;
  }

  return directParentRead(scope);
};

/** State to pass to `navigate` so the record page opens through the parent path. */
export const parentChildReadState = (
  scope: ParentPointRead | undefined,
): { parentChildRead: ParentScopedChildQuery } | undefined => {
  const read = scope ? readFields({ ...scope }) : undefined;

  return read ? { parentChildRead: read } : undefined;
};

/** The parent-path point read carried on `location.state`, if this navigation has one. */
export const parentChildReadFromState = (state: unknown): ParentScopedChildQuery | undefined => {
  if (!isPlainRecord(state) || !(PARENT_CHILD_READ_STATE in state)) {
    return undefined;
  }

  const value = state[PARENT_CHILD_READ_STATE];
  if (!isPlainRecord(value)) {
    return undefined;
  }

  const text = (key: string): string | undefined =>
    typeof value[key] === "string" ? value[key] : undefined;
  const parentResource = text("parentResource");
  const parentVersion = text("parentVersion");
  const parentPk = text("parentPk");
  const relationship = text("relationship");
  const relationshipVersion = text("relationshipVersion");
  const childResource = text("childResource");
  const childVersion = text("childVersion");
  if (
    !parentResource ||
    !parentVersion ||
    !parentPk ||
    !relationship ||
    !relationshipVersion ||
    !childResource ||
    !childVersion
  ) {
    return undefined;
  }

  return directParentRead({
    parentResource,
    parentVersion,
    parentPk,
    relationship,
    relationshipVersion,
    childResource,
    childVersion,
  });
};

/**
 * The owned-child read returns the Postgres record. `customFetch` already
 * unwraps a pure `{ data }` envelope, so the body is the business object.
 * A metadata envelope is accepted too. A miss is `null` — never a second
 * request to the global record route.
 */
/** Same shape the global detail hook exposes, including audit metadata when the body has it. */
export type ChildUnderParentRecord = Record<string, unknown> & {
  _metadata?: BusinessObjectMetadata;
};

const childRecordFromBody = (raw: unknown): ChildUnderParentRecord | null => {
  if (!isPlainRecord(raw)) {
    return null;
  }

  if (isPlainRecord(raw.data) && "metadata" in raw) {
    const parsed = parseRecordDetail(raw);
    if (!parsed) {
      return null;
    }

    return {
      ...parsed.data,
      _metadata: extractAuditMetadata(parsed),
    };
  }

  return raw;
};

/**
 * `GET …/children/{child}/{childVersion}/{childPk}`.
 * 404 and 403 are misses. Any other status is an error. There is no fallback.
 */
export const fetchChildUnderParent = async (
  scope: ParentScopedChildQuery,
  childPk: string,
  signal?: AbortSignal,
): Promise<ChildUnderParentRecord | null> => {
  const read = directParentRead(scope);
  if (!read || childPk.trim() === "") {
    throw new Error("Parent-path point read is only defined for a direct outgoing edge");
  }

  const response = await getChildUnderParent(
    ...oneHopArgs(read),
    segment(childPk),
    signal ? { signal } : undefined,
  );

  if (response.status === 404 || response.status === 403) {
    return null;
  }

  if (response.status !== 200) {
    throw new ApiError({
      status: response.status,
      message: "Couldn't open this record under its parent",
      method: "GET",
    });
  }

  return childRecordFromBody(response.data);
};
